<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useRoute } from "vue-router";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import Button from "@/components/ui/button/Button.vue";
import FileTag from "@/components/common/tags/FileTag.vue";
import { useUserStore } from "@/stores/user";
import type { FileAttachment, Thumbnail } from "@/bapi-client/types/common/attachment";
import type { GenericNote } from "@/bapi-client/types";

interface Props {
  note: GenericNote;
  thumbnails?: Thumbnail[];
  files?: FileAttachment[];
  subtitle?: string;
  isInternal?: boolean;
  isClickable?: boolean;
  showAttachments?: boolean;
  withMenu?: boolean;
}

const confirmDelete = ref(false);
const isDropdownOpen = ref(false);

const userStore = useUserStore();
const route = useRoute();

const props = withDefaults(defineProps<Props>(), {
  showAttachments: true,
  withMenu: false,
  isClickable: false,
  subtitle: "",
  thumbnails: () => [],
  files: () => [],
});

const emit = defineEmits<{
  (event: "download-file", value: string): void;
  (event: "preview-file", value: string): void;
  (event: "delete-note", value: string): void;
  (event: "edit-note", value: string): void;
}>();

async function deleteNote() {
  confirmDelete.value = false;
  emit("delete-note", props.note.id);
}

watch(isDropdownOpen, (isOpen) => {
  if (!isOpen) {
    confirmDelete.value = false;
  }
});

const fileCount = computed(() => props.note.attachments?.length || 0);
</script>

<template>
  <li class="w-full py-3 first:pt-0" :class="{ 'hover:cursor-pointer': isClickable }">
    <div class="flex items-start gap-3">
      <Avatar>
        <AvatarImage
          :src="props.note.signed_profile_pictures?.thumbnail_url ?? ''"
          :alt="`${props.note.user_name} profile picture`"
        />
        <AvatarFallback>
          <i v-if="!props.note.user_id || props.note.user_id.length < 1" class="fa-solid fa-user"></i>
          <span v-else class="inline-flex h-full w-full items-center justify-center rounded-full bg-gray-500">
            <span class="text-xs leading-none font-medium text-white">{{ props.note.user_initials }}</span>
          </span>
        </AvatarFallback>
      </Avatar>
      <div class="flex w-full flex-col">
        <p class="text-sm font-bold text-gray-900">
          {{ isInternal ? props.note.user_name + " - Internal Only" : props.note.user_name }}
        </p>
        <p class="inline-block text-sm">
          <span v-if="subtitle" data-testid="note-subtitle" class="mr-1 font-semibold text-gray-600">
            {{ subtitle }}
          </span>
          <span class="mr-0.5 font-light text-gray-600">
            {{
              props.note.timestamp
                ? $date("MM/DD HH:mm", props.note.timestamp, true)
                : $date("MM/DD HH:mm", props.note.created_ts, true)
            }}
          </span>
          <TooltipProvider class="bg-amber-100">
            <Tooltip>
              <TooltipTrigger v-if="props.note.updated_ts" class="font-light text-gray-600"> (edited) </TooltipTrigger>
              <TooltipContent color="muted" text-color="primary" side="right" :side-offset="-4">
                {{ $date("MM/DD HH:mm", props.note.updated_ts) }}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </p>
        <p v-if="props.note.note_text || props.note.note" class="mt-3 text-sm text-gray-700" data-test="note-text">
          {{ props.note.note_text || props.note.note }}
        </p>
      </div>
      <DropdownMenu v-if="withMenu && props.note.user_id === userStore.user?.user_id" v-model:open="isDropdownOpen">
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" size="icon" @click.stop>
            <i class="fa-solid fa-ellipsis-vertical"></i>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem
            v-if="typeof route.name === 'string' && !route.name.includes('pricing')"
            @click.stop.prevent="$emit('edit-note', props.note.id)"
          >
            <span><i class="fa-solid fa-edit mr-2" /></span>
            Edit Note
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" @click.stop.prevent="confirmDelete = true" @select.prevent>
            <span><i class="fa-solid fa-trash-can mr-2" /></span>
            Delete Note
          </DropdownMenuItem>
          <DropdownMenuItem v-if="confirmDelete" variant="destructive" @click.stop.prevent="deleteNote">
            <span><i class="fa-solid fa-trash-can-check mr-2" /></span>
            Confirm
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    <template v-if="showAttachments === true">
      <!-- Show thumbnails if provided -->
      <div v-if="thumbnails && thumbnails.length" class="ml-12 flex flex-row flex-wrap items-start gap-2 pt-4 pl-2">
        <template v-for="thumbnail in thumbnails" :key="thumbnail.id">
          <span
            v-if="thumbnail.loading === true"
            class="text-primary flex h-24 w-48 flex-col items-center justify-center rounded-md border text-center align-middle"
          >
            <i class="fa-solid fa-circle-notch fa-spin fa-2x"></i>
          </span>
          <img
            v-if="thumbnail.loading === false && thumbnail.src"
            :src="thumbnail.src"
            width="200"
            class="hover:ring-primary rounded-md border transition hover:shadow-md hover:ring"
            @click="$emit('preview-file', thumbnail.id)"
          />
        </template>
      </div>

      <!-- Show files if provided -->
      <div v-if="files && files.length" class="ml-10 flex flex-row flex-wrap items-start gap-2 pt-4 pl-2">
        <template v-for="file in files" :key="file.id">
          <FileTag
            v-if="file.mimeType.startsWith('video/') || file.mimeType.startsWith('image/')"
            :file="file"
            @click="$emit('preview-file', file.id)"
          />
          <FileTag v-else :file="file" @click="$emit('download-file', file.id)" />
        </template>
      </div>
    </template>
    <div v-else>
      <p v-if="fileCount > 0" class="mt-1 pl-12 text-sm text-gray-600" data-test="note-text">
        <span class="font-medium text-gray-600">
          <i class="fa-light fa-paperclip"></i>
        </span>
        {{ fileCount }}
      </p>
    </div>
  </li>
</template>
